import React, { Fragment } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Icon,
  IconButton,
  Tooltip,
  alpha,
  Drawer,
  Divider,
} from "@mui/material";
import { toast } from "react-toastify";
import { inventoryService } from "../../services/inventory.service";
import { useDebounce } from "../../hooks/useDebounce";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { FilterGroup, IESDoc, TableColumn } from "../../../../app.interface";
import { EmptyState } from "../../../../components/EmptyState/EmptyState";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { IngredientDetails } from "../../components/IngredientDetails/IngredientDetails";
import { DeleteConfirmationDialog } from "../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { AppTable } from "../../../../components/AppTable";
import { AppSearch } from "../../../../components/AppSearch";

const StyledProgressBar = styled(Box)(({ theme }) => ({
  position: "relative",
  height: 20,
  borderRadius: 5,
  backgroundColor: alpha(theme.palette.grey[200], 0.3),
  overflow: "hidden",
  "& .bar": {
    position: "absolute",
    left: 0,
    top: 0,
    height: "100%",
    borderRadius: 5,
    transition: "all 0.3s ease",
  },
  "& .indicator": {
    position: "absolute",
    left: 0,
    top: 0,
    width: 2,
    height: "100%",
    backgroundColor: "#F59E0B",
    borderRadius: "50%",
    transform: "translateX(-50%)",
    transition: "all 0.2s",
    zIndex: 2,
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: -4,
      left: -3,
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: "#F59E0B",
      boxShadow: `0 0 0 2px ${alpha("#F59E0B", 0.2)}`,
    },
    "&:hover": {
      width: 3,
      "&::after": {
        transform: "scale(1.2)",
      },
    },
  },
}));

const DetailItem: React.FC<{
  icon: string;
  label: string;
  value: string;
  valueColor?: string;
}> = ({ icon, label, value, valueColor }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 0.5,
        fontFamily: "Montserrat",
      }}
    >
      <Icon sx={{ fontSize: 16 }}>{icon}</Icon>
      {label}
    </Typography>
    <Typography
      variant="body1"
      sx={{
        fontWeight: 500,
        color: valueColor || "text.primary",
        fontFamily: "Montserrat",
      }}
    >
      {value}
    </Typography>
  </Box>
);

export const Ingredients: React.FC = () => {
  const [ingredients, setIngredients] = React.useState<IESDoc[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [category, setCategory] = React.useState("all");
  const [stockStatus, setStockStatus] = React.useState("all");
  const [sortField, setSortField] = React.useState("created_at");
  const [pageSize, setPageSize] = React.useState(25);
  const [pageIndex, setPageIndex] = React.useState(0);
  const [totalItems, setTotalItems] = React.useState(0);
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("asc");
  const [isIngredientFormOpen, setIsIngredientFormOpen] = React.useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = React.useState(false);
  const [selectedIngredient, setSelectedIngredient] = React.useState<
    IESDoc | undefined
  >(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedIngredientForDelete, setSelectedIngredientForDelete] =
    React.useState<IESDoc | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  const [filters, setFilters] = React.useState<Record<string, any>>({});
  const debouncedSearch = useDebounce(search, 500);
  const navigate = useNavigate();

  const filterGroups: FilterGroup[] = [
    {
      id: "category",
      label: "Category",
      type: "select",
      icon: "category",
      description: "Filter ingredients by their category",
      options: [
        {
          id: "dairy",
          label: "Dairy",
          value: "dairy",
          icon: "breakfast_dining",
          color: "#0891B2",
        },
        {
          id: "grains",
          label: "Grains",
          value: "grains",
          icon: "grain",
          color: "#0891B2",
        },
        {
          id: "spices",
          label: "Spices",
          value: "spices",
          icon: "spa",
          color: "#0891B2",
        },
        {
          id: "produce",
          label: "Produce",
          value: "produce",
          icon: "eco",
          color: "#0891B2",
        },
        {
          id: "meat",
          label: "Meat",
          value: "meat",
          icon: "restaurant",
          color: "#0891B2",
        },
        {
          id: "seafood",
          label: "Seafood",
          value: "seafood",
          icon: "set_meal",
          color: "#0891B2",
        },
        {
          id: "oils",
          label: "Oils & Fats",
          value: "oils",
          icon: "water_drop",
          color: "#0891B2",
        },
      ],
    },
    {
      id: "stock_status",
      label: "Stock Status",
      type: "select",
      icon: "inventory",
      description: "Filter ingredients by their stock level",
      options: [
        {
          id: "in_stock",
          label: "In Stock",
          value: "in_stock",
          icon: "check_circle",
          color: "#059669",
        },
        {
          id: "low_stock",
          label: "Low Stock",
          value: "low_stock",
          icon: "warning",
          color: "#F59E0B",
        },
        {
          id: "out_of_stock",
          label: "Out of Stock",
          value: "out_of_stock",
          icon: "error",
          color: "#DC2626",
        },
      ],
    },
    {
      id: "sortBy",
      label: "Sort By",
      type: "select",
      icon: "sort",
      description: "Sort ingredients by field",
      options: [
        {
          id: "name_asc",
          label: "Name (A-Z)",
          value: "name_asc",
          icon: "arrow_upward",
        },
        {
          id: "name_desc",
          label: "Name (Z-A)",
          value: "name_desc",
          icon: "arrow_downward",
        },
        {
          id: "stock_asc",
          label: "Stock (Low to High)",
          value: "stock_asc",
          icon: "trending_up",
        },
        {
          id: "stock_desc",
          label: "Stock (High to Low)",
          value: "stock_desc",
          icon: "trending_down",
        },
      ],
    },
  ];

  const MasterColumns: TableColumn[] = [
    {
      id: "ingredient_id",
      header: "ID & Details",
      field: "ingredient_id",
      type: "action",
      width: "250px",
      renderAction: (row: any) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
            {row.name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Icon fontSize="inherit">qr_code</Icon>
              {row.sku}
            </Typography>
            <Tooltip title="Copy SKU">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(row.sku);
                  toast.success("SKU copied to clipboard");
                }}
                sx={{
                  p: 0.5,
                  color: "#0891B2",
                  "&:hover": {
                    bgcolor: alpha("#0891B2", 0.08),
                  },
                }}
              >
                <Icon fontSize="inherit">content_copy</Icon>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ),
    },
    {
      id: "category",
      header: "Category",
      field: "category",
      type: "action",
      width: "150px",
      renderAction: (row: any) => (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            bgcolor: alpha("#0891B2", 0.08),
            color: "#0891B2",
            border: "1px solid",
            borderColor: alpha("#0891B2", 0.2),
          }}
        >
          <Icon sx={{ fontSize: 16 }}>category</Icon>
          {row.category}
        </Box>
      ),
    },
    {
      id: "stock",
      header: "Stock Status",
      field: "stock_quantity",
      type: "action",
      width: "300px",
      renderAction: (row: any) => {
        const stockPercentage =
          (row.stock_quantity / (row.max_stock_level || 1)) * 100;
        const getStockColor = (percentage: number) => {
          if (percentage >= 75) return "#059669"; // Green for >= 75%
          if (percentage >= 50) return "#0891B2"; // Cyan for >= 50%
          if (percentage >= 25) return "#F59E0B"; // Orange for >= 25%
          return "#DC2626"; // Red for < 25%
        };
        const stockColor = getStockColor(stockPercentage);

        return (
          <Box sx={{ minWidth: 220 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: row.is_out_of_stock
                    ? "#DC2626"
                    : row.is_low_stock
                    ? "#F59E0B"
                    : "#059669",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Icon fontSize="small">
                  {row.is_out_of_stock
                    ? "error"
                    : row.is_low_stock
                    ? "warning"
                    : "check_circle"}
                </Icon>
                {row.stock_quantity.toLocaleString()} {row.unit}
              </Typography>
              <Box
                sx={{
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: row.is_out_of_stock
                    ? alpha("#DC2626", 0.08)
                    : row.is_low_stock
                    ? alpha("#F59E0B", 0.08)
                    : alpha("#059669", 0.08),
                  color: row.is_out_of_stock
                    ? "#DC2626"
                    : row.is_low_stock
                    ? "#F59E0B"
                    : "#059669",
                  border: "1px solid",
                  borderColor: row.is_out_of_stock
                    ? alpha("#DC2626", 0.2)
                    : row.is_low_stock
                    ? alpha("#F59E0B", 0.2)
                    : alpha("#059669", 0.2),
                  fontSize: "0.75rem",
                  fontWeight: 500,
                }}
              >
                {row.is_out_of_stock
                  ? "Out of Stock"
                  : row.is_low_stock
                  ? "Low Stock"
                  : "In Stock"}
              </Box>
            </Box>
            <Box sx={{ position: "relative" }}>
              <StyledProgressBar>
                <Box
                  className="bar"
                  sx={{
                    width: `${Math.min(stockPercentage, 100)}%`,
                    bgcolor: alpha(stockColor, 0.8),
                    boxShadow: `0 0 8px ${alpha(stockColor, 0.4)}`,
                    backgroundImage: `linear-gradient(90deg, ${alpha(
                      stockColor,
                      0.6
                    )} 0%, ${stockColor} 100%)`,
                  }}
                />
                {row.min_stock_level > 0 && (
                  <Box
                    className="indicator"
                    sx={{
                      left: `${
                        (row.min_stock_level / (row.max_stock_level || 1)) * 100
                      }%`,
                    }}
                  />
                )}
              </StyledProgressBar>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  color: "white",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                  zIndex: 1,
                  pointerEvents: "none",
                }}
              >
                {Math.round(stockPercentage)}%
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "#F59E0B",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Icon fontSize="inherit">warning</Icon>
                Min: {row.min_stock_level.toLocaleString()} {row.unit}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#059669",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Icon fontSize="inherit">inventory</Icon>
                Max: {row.max_stock_level.toLocaleString()} {row.unit}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      id: "allergens",
      header: "Allergens & Type",
      field: "allergens",
      type: "action",
      width: "200px",
      renderAction: (row: any) =>
        row.allergens && row.allergens.length > 0 ? (
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            <Box
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: alpha("#F59E0B", 0.08),
                color: "#F59E0B",
                border: "1px solid",
                borderColor: alpha("#F59E0B", 0.2),
                fontSize: "0.75rem",
                fontWeight: 500,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Icon sx={{ fontSize: 16 }}>warning_amber</Icon>
              {row.allergens[0].charAt(0).toUpperCase() +
                row.allergens[0].slice(1)}
            </Box>
            {row.allergens.length > 1 && (
              <Tooltip
                title={row.allergens
                  .slice(1)
                  .map((a: string) => a.charAt(0).toUpperCase() + a.slice(1))
                  .join(", ")}
                arrow
              >
                <Box
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: alpha("#F59E0B", 0.08),
                    color: "#F59E0B",
                    border: "1px solid",
                    borderColor: alpha("#F59E0B", 0.2),
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  +{row.allergens.length - 1}
                </Box>
              </Tooltip>
            )}
          </Box>
        ) : (
          <Typography
            variant="caption"
            sx={{
              color: "#059669",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Icon fontSize="small">check_circle</Icon>
            Allergen Free
          </Typography>
        ),
    },

    {
      id: "actions",
      header: "",
      field: "actions",
      type: "action",
      width: "120px",
      renderAction: (row: any) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="View Batches" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  `/business/stock-management/raw-materials/${row.ingredient_id}/batches`
                );
              }}
              sx={{
                width: 34,
                height: 34,
                bgcolor: alpha("#0891B2", 0.08),
                color: "#0891B2",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: alpha("#0891B2", 0.12),
                  boxShadow: "0 4px 8px rgba(8, 145, 178, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>inventory_2</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row);
              }}
              sx={{
                width: 34,
                height: 34,
                bgcolor: alpha("#0891B2", 0.08),
                color: "#0891B2",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: alpha("#0891B2", 0.12),
                  boxShadow: "0 4px 8px rgba(8, 145, 178, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>edit_outlined</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteDialog(row);
              }}
              sx={{
                width: 34,
                height: 34,
                bgcolor: alpha("#DC2626", 0.08),
                color: "#DC2626",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: alpha("#DC2626", 0.12),
                  boxShadow: "0 4px 8px rgba(220, 38, 38, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>delete_outlined</Icon>
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const loadIngredients = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await inventoryService.listIngredients({
        searchQuery: debouncedSearch,
        filters: [],
        sortField,
        sortOrder,
        pageIndex,
        pageSize,
      });
      setIngredients(response.data);
      setTotalItems(response.metadata.total.value);
    } catch (error) {
      console.error("Error loading raw materials:", error);
      toast.error("Failed to load raw materials");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, sortField, sortOrder, pageIndex, pageSize]);

  React.useEffect(() => {
    loadIngredients();
  }, [loadIngredients]);

  const handleEdit = (ingredient: IESDoc) => {
    navigate(`/business/stock-management/raw-materials/${ingredient._id}/edit`);
  };

  const handleOpenDeleteDialog = (ingredient: IESDoc) => {
    setSelectedIngredientForDelete(ingredient);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedIngredientForDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleDelete = async () => {
    if (!selectedIngredientForDelete) return;
    try {
      setDeleting(true);
      await inventoryService.deleteIngredient(selectedIngredientForDelete._id);
      handleCloseDeleteDialog();
      loadIngredients();
      toast.success("Raw material deleted successfully");
    } catch (error) {
      toast.error("Error deleting raw material");
    } finally {
      setDeleting(false);
    }
  };

  const handleAddIngredient = () => {
    navigate("/business/stock-management/raw-materials/new");
  };

  const handleRecalculateStock = async () => {
    try {
      setLoading(true);
      await inventoryService.recalculateIngredientStock();
      toast.success("Stock levels recalculated successfully");
      loadIngredients();
    } catch (error) {
      console.error("Error recalculating stock levels:", error);
      toast.error("Failed to recalculate stock levels");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Raw Materials"
        subtitle="Manage your kitchen's raw materials and ingredients for recipe preparation"
        icon="spa"
        actions={
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Icon>sync</Icon>}
              onClick={handleRecalculateStock}
              sx={{
                borderColor: "#0891B2",
                color: "#0891B2",
                "&:hover": {
                  borderColor: "#0E7490",
                  bgcolor: alpha("#0891B2", 0.04),
                },
              }}
            >
              Recalculate Stock
            </Button>
            <Button
              variant="contained"
              startIcon={<Icon>add</Icon>}
              onClick={handleAddIngredient}
              sx={{
                bgcolor: "#0891B2",
                "&:hover": {
                  bgcolor: "#0E7490",
                },
              }}
            >
              Add Raw Material
            </Button>
          </Box>
        }
      />

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppSearch
          placeholder="Search raw materials by name or SKU..."
          maxWidth="100%"
          onSearch={setSearch}
          onFilterChange={setFilters}
          filterGroups={filterGroups}
          defaultFilters={filters}
        />
      </Paper>

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppTable
          data={ingredients.map((ingredient) => ({
            ...ingredient,
            status: ingredient.is_deleted ? "inactive" : "active",
          }))}
          allowHeaderWrap
          columns={MasterColumns}
          variant="default"
          sortConfig={{ field: sortField, direction: sortOrder }}
          onSort={handleSort}
          loading={loading}
          header={{
            title: "Raw Materials List",
            subtitle: "Your current list of raw materials and ingredients",
          }}
          selectable
          onSelectionChange={(selected) => {
            console.log("Selected:", selected);
          }}
          styleOptions={{
            bordered: true,
            columnSeparators: true,
            headerBackground: alpha("#0891B2", 0.02),
            rowHoverBackground: alpha("#0891B2", 0.04),
            borderColor: alpha("#0891B2", 0.1),
          }}
          toolbarActions={{
            refresh: {
              label: "Refresh List",
              is_disabled: loading,
              onClick: () => loadIngredients(),
            },
            export: {
              label: "Export Raw Materials",
              is_disabled: loading,
              options: {
                formats: ["csv", "excel", "json"],
                filename: "raw-materials-list",
              },
            },
          }}
          pagination={{
            total: totalItems,
            page: pageIndex,
            pageSize: pageSize,
            onPageChange: setPageIndex,
            onPageSizeChange: setPageSize,
          }}
          onRowClick={(row) => {
            setSelectedIngredient(row);
            setDetailsDrawerOpen(true);
          }}
          rowProps={{
            sx: {
              cursor: "pointer",
              "&:hover": {
                bgcolor: alpha("#0891B2", 0.04),
              },
            },
          }}
        />
      </Paper>

      <Drawer
        anchor="right"
        open={detailsDrawerOpen}
        onClose={() => {
          setDetailsDrawerOpen(false);
          setSelectedIngredient(undefined);
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxWidth: "100%",
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            overflow: "hidden",
            borderLeft: "1px solid",
            borderColor: alpha("#0891B2", 0.1),
          },
        }}
      >
        {selectedIngredient && (
          <Fragment>
            <Box
              sx={{
                px: 3,
                py: 2,
                background: "#0891B2",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Icon>spa</Icon>
                <Typography variant="subtitle1" fontWeight={600}>
                  Ingredient Details
                </Typography>
              </Box>
              <IconButton
                onClick={() => {
                  setDetailsDrawerOpen(false);
                  setSelectedIngredient(undefined);
                }}
                size="small"
                sx={{
                  color: "white",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <Icon>close</Icon>
              </IconButton>
            </Box>

            <Box sx={{ p: 3 }}>
              <Box display="grid" gap={3}>
                <DetailItem
                  icon="tag"
                  label="SKU"
                  value={selectedIngredient.sku}
                />
                <DetailItem
                  icon="spa"
                  label="Name"
                  value={selectedIngredient.name}
                />
                {selectedIngredient.description && (
                  <DetailItem
                    icon="description"
                    label="Description"
                    value={selectedIngredient.description}
                  />
                )}
                <DetailItem
                  icon="category"
                  label="Category"
                  value={selectedIngredient.category}
                />
                <DetailItem
                  icon="inventory"
                  label="Stock Level"
                  value={`${selectedIngredient.stock_quantity.toLocaleString()} ${
                    selectedIngredient.unit
                  }`}
                  valueColor={
                    selectedIngredient.is_out_of_stock
                      ? "#DC2626"
                      : selectedIngredient.is_low_stock
                      ? "#F59E0B"
                      : "#059669"
                  }
                />
                {selectedIngredient.base_unit &&
                  selectedIngredient.conversion_factor && (
                    <DetailItem
                      icon="swap_horiz"
                      label="Unit Conversion"
                      value={`1 ${selectedIngredient.unit} = ${selectedIngredient.conversion_factor} ${selectedIngredient.base_unit}`}
                    />
                  )}
                {selectedIngredient.storage_instructions && (
                  <DetailItem
                    icon="info"
                    label="Storage Instructions"
                    value={selectedIngredient.storage_instructions}
                  />
                )}
                {selectedIngredient.expiry_date && (
                  <DetailItem
                    icon="event"
                    label="Expiry Date"
                    value={new Date(
                      selectedIngredient.expiry_date
                    ).toLocaleDateString()}
                  />
                )}
              </Box>

              {selectedIngredient.allergens &&
                selectedIngredient.allergens.length > 0 && (
                  <>
                    <Divider
                      sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }}
                    />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        color: "text.primary",
                        fontFamily: "Montserrat",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Icon sx={{ fontSize: 20 }}>warning_amber</Icon>
                      Allergens
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {selectedIngredient.allergens.map((allergen: string) => (
                        <Box
                          key={allergen}
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: alpha("#F59E0B", 0.08),
                            color: "#F59E0B",
                            border: "1px solid",
                            borderColor: alpha("#F59E0B", 0.2),
                            fontSize: "0.75rem",
                            fontWeight: 500,
                          }}
                        >
                          {allergen.charAt(0).toUpperCase() + allergen.slice(1)}
                        </Box>
                      ))}
                    </Box>
                  </>
                )}

              <Divider sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }} />

              <Box display="flex" gap={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Icon>edit</Icon>}
                  onClick={() => {
                    handleEdit(selectedIngredient);
                    setDetailsDrawerOpen(false);
                  }}
                  sx={{
                    borderColor: "#0891B2",
                    color: "#0891B2",
                    "&:hover": {
                      borderColor: "#0E7490",
                      bgcolor: alpha("#0891B2", 0.04),
                    },
                  }}
                >
                  Edit Ingredient
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<Icon>delete</Icon>}
                  onClick={() => {
                    handleOpenDeleteDialog(selectedIngredient);
                    setDetailsDrawerOpen(false);
                  }}
                  sx={{
                    borderColor: "#DC2626",
                    color: "#DC2626",
                    "&:hover": {
                      borderColor: "#B91C1C",
                      bgcolor: alpha("#DC2626", 0.04),
                    },
                  }}
                >
                  Delete Ingredient
                </Button>
              </Box>
            </Box>
          </Fragment>
        )}
      </Drawer>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDelete}
        title="Delete Raw Material"
        itemName={selectedIngredientForDelete?.name || ""}
        itemDescription={`${selectedIngredientForDelete?.category || ""} - ${
          selectedIngredientForDelete?.stock_quantity || 0
        } ${selectedIngredientForDelete?.unit || ""}`}
        loading={deleting}
        icon="spa"
      />
    </Box>
  );
};
